import { ChangeEvent, useEffect, useReducer, useState } from "react";
import { Filter } from "../types/PageFilterTypes";
import {
  CreateTaskConfig,
  SubjectType,
  Task,
  TaskPriority,
  TaskStatus,
} from "../models/workflowModels";
import { usePost } from "./usePost";
import { UserSimple } from "../models/userModels";
import { getAvatarOption } from "../components/visuals/Avatar";
import {
  isSystemUser,
  resolveDisplayName,
  SYSTEM_USER_DISPLAY_NAME,
} from "../constants";
import {
  CommonDataItem,
  CommonDataRequestType,
  DistinctFundsRequest,
  FundsWManagerItem,
} from "../types/CommonDataRequestTypes";
import {
  PRIORITY_MAPPING,
  STATUS_MAPPING,
  SUBJECT_TYPE_OPTIONS,
} from "../pages/Workspace/constants";
import { Dayjs } from "dayjs";
import { DATE_OPTION_KEY } from "../components/visuals/PageFilter/PageFilter";
import { getCalendarPickerOption } from "../components/common/Voting/CalendarPicker";
import { formatReadable } from "../utils/formattingUtils";
import { useSearchParams } from "react-router-dom";
import { MESSAGES } from "../messages";

enum ActionType {
  UPDATE_TITLE = "updateTitle",
  UPDATE_ASSIGNEE = "updateAssignee",
  UPDATE_PRIORITY = "updatePriority",
  UPDATE_SUBJECT_TYPE = "updateSubjectType",
  UPDATE_SUBJECTS = "updateSubjects",
  UPDATE_DUE_DATE = "updateDueDate",
  RESET_PARAMS = "resetParams",
}

export type Action =
  | { type: ActionType.UPDATE_TITLE; payload: string }
  | { type: ActionType.UPDATE_ASSIGNEE; payload: Filter }
  | { type: ActionType.UPDATE_PRIORITY; payload: Filter }
  | { type: ActionType.UPDATE_SUBJECT_TYPE; payload: Filter }
  | { type: ActionType.UPDATE_SUBJECTS; payload: Filter[] }
  | { type: ActionType.UPDATE_DUE_DATE; payload: Filter }
  | { type: ActionType.RESET_PARAMS };

const BASE_PARAMETERS: CreateTaskConfig = {
  key: 1,
  title: null,
  assignee: null,
  priority: TaskPriority.LOW,
  subjectType: null,
  subjects: [],
  dueDate: null,
};

const BASE_DATE_OPTION: Filter = {
  key: DATE_OPTION_KEY,
  displayValue: "Select date",
};
const reducer = (state: CreateTaskConfig, action: Action): CreateTaskConfig => {
  switch (action.type) {
    case ActionType.UPDATE_TITLE:
      return {
        ...state,
        title: action.payload,
      };

    case ActionType.UPDATE_ASSIGNEE:
      return {
        ...state,
        assignee: action.payload.displayValue,
      };

    case ActionType.UPDATE_SUBJECT_TYPE:
      return {
        ...state,
        subjectType: action.payload.key as SubjectType,
      };

    case ActionType.UPDATE_SUBJECTS:
      return {
        ...state,
        subjects: action.payload.map((subject) => subject.key as string),
      };

    case ActionType.UPDATE_DUE_DATE:
      return {
        ...state,
        dueDate: action.payload.displayValue,
      };

    case ActionType.UPDATE_PRIORITY:
      return {
        ...state,
        priority: action.payload.key as TaskPriority,
      };

    case ActionType.RESET_PARAMS:
      return { ...BASE_PARAMETERS, key: state.key + 1 };

    default:
      console.error("Invalid action type");
      return state;
  }
};

export const useTaskModal = (): TaskModalProps => {
  const [state, dispatch] = useReducer(reducer, BASE_PARAMETERS);
  const [subjectsOptions, setSubjectsOptions] = useState<Filter[]>([]);
  const [overrides, setOverrides] = useState<Record<string, Filter>>({});
  const [dateOptions, setDateOptions] = useState<Filter[]>([
    {
      ...BASE_DATE_OPTION,
      component: () =>
        getCalendarPickerOption({ onSelectDate: handleDateSelect }),
    },
  ]);
  const [status, setStatus] = useState<TaskStatus | null>(null);
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");

  const shouldDisplaySubjectOptions =
    state.subjectType !== null && state.subjectType !== SubjectType.NONE;

  const selectedSubjectTypeDisplayValue: string =
    SUBJECT_TYPE_OPTIONS.find((option) => option.value === state.subjectType)
      ?.label || SUBJECT_TYPE_OPTIONS[0].label;

  const {
    data: users,
    isLoading: isUsersLoading,
    sendData: getUsers,
  } = usePost<UserSimple[], null>();

  const {
    data: distinctFunds,
    sendData: fetchDistinctFunds,
    isLoading: isDistinctFundsLoadings,
  } = usePost<FundsWManagerItem[], CommonDataItem<DistinctFundsRequest>>();

  const {
    data: taskData,
    sendData: getTask,
    isLoading: isTaskLoading,
  } = usePost<Task[], { task_id: string }>();

  const {
    data: companies,
    isLoading: isCompaniesLoading,
    sendData: getCompanies,
  } = usePost<{ name: string; compass_cid: string }[], null>();

  const isSubjectTypeLoading = isDistinctFundsLoadings || isCompaniesLoading;

  useEffect(() => {
    !users && getUsers("GET_USERS", null);
  }, []);

  useEffect(() => {
    taskId &&
      getTask("GET_TASKS", {
        task_id: taskId,
      });
  }, [taskId]);

  useEffect(() => {
    if (taskData && taskData.length > 0) {
      buildConfigFromFetchedTask(taskData[0]);
      users && buildOverrides(taskData[0]);
    }
  }, [taskData, users]);

  useEffect(() => {
    if (!distinctFunds && state.subjectType === SubjectType.FUNDS) {
      fetchDistinctFunds("GET_COMMON_DATA", {
        name: CommonDataRequestType.DISTINCT_FUNDS_W_MANAGER,
        item: { distinct_funds: null },
      });
    }
    if (!companies && state.subjectType === SubjectType.COMPANY) {
      getCompanies("GET_COMPANIES", null);
    }
  }, [state.subjectType]);

  useEffect(() => {
    if (distinctFunds && state.subjectType === SubjectType.FUNDS) {
      setSubjectsOptions(buildFundOptions());
    }
    if (companies && state.subjectType === SubjectType.COMPANY) {
      setSubjectsOptions(buildCompanyOptions());
    }
  }, [distinctFunds, state.subjectType, companies]);

  useEffect(() => {
    dispatchDueDateUpdate(dateOptions[0]);
  }, [dateOptions]);

  const buildConfigFromFetchedTask = (task: Task): void => {
    dispatchTitleUpdate({
      target: { value: task.title },
    } as unknown as ChangeEvent<HTMLTextAreaElement | HTMLInputElement>);
    dispatchAssigneeUpdate({
      key: task.assigned_to,
      displayValue: resolveDisplayName(task.assigned_to),
    });
    dispatchPriorityUpdate({ key: task.priority, displayValue: task.priority });
    dispatchSubjectTypeUpdate({
      key: task.subject_type,
      displayValue: task.subject_type,
    });
    dispatchSubjectsUpdate(
      task.notes.map((subject) => ({
        key: subject?.note_id || null,
        displayValue: subject?.title || MESSAGES.NOT_AVAILABLE,
      })),
    );
    dispatchDueDateUpdate({ key: task.date_due, displayValue: task.date_due });
  };

  const handleStatusChange = (option: Filter): void => {
    setStatus(option.key as TaskStatus);
  };

  const buildOverrides = (taskData: Task): void => {
    const userOptions = buildUserOptions();

    const assigneeOverride: Filter = isSystemUser(taskData.assigned_to)
      ? {
          key: taskData.assigned_to,
          displayValue: SYSTEM_USER_DISPLAY_NAME,
          component: () => getAvatarOption({ name: SYSTEM_USER_DISPLAY_NAME }),
        }
      : userOptions.find((user) => user.key === taskData.assigned_to) ||
        userOptions[0];
    const priorityOverride: Filter = {
      key: taskData.priority,
      displayValue: PRIORITY_MAPPING[taskData.priority],
    };

    const statusOverride: Filter = {
      key: taskData.status,
      displayValue: STATUS_MAPPING[taskData.status],
    };

    const retrievedDueDate: string = taskData.date_due.slice(0, 10);
    const dueDateOverrideValue: string =
      retrievedDueDate === MESSAGES.NOT_AVAILABLE
        ? "Select due date"
        : retrievedDueDate;

    const dueDateOverride: Filter = {
      key: DATE_OPTION_KEY,
      displayValue: dueDateOverrideValue,
      component: () =>
        getCalendarPickerOption({ onSelectDate: handleDateSelect }),
    };

    setDateOptions([dueDateOverride]);

    setOverrides({
      assignee: assigneeOverride,
      priority: priorityOverride,
      status: statusOverride,
      date: dueDateOverride,
    });
  };

  const buildCompanyOptions = (): Filter[] =>
    companies
      ?.sort((a, b) => a.name.localeCompare(b.name))
      .map((company) => ({
        key: company.compass_cid,
        displayValue: formatReadable(company.name),
      })) || [];

  const buildUserOptions = (): Filter[] => {
    return (
      users?.map((user) => ({
        key: user.username,
        displayValue: user.username,
        component: () => getAvatarOption({ name: user.name }),
      })) || []
    );
  };

  const buildFundOptions = (): Filter[] => {
    return (
      distinctFunds?.map((fund) => ({
        key: fund.fund_id,
        displayValue: fund.fund_id,
      })) || []
    );
  };

  const handleDateSelect = (date: Dayjs): void => {
    setDateOptions((prev) => {
      return prev.map((option) => {
        if (option.key === DATE_OPTION_KEY) {
          return {
            ...option,
            displayValue: date.format("YYYY-MM-DD"),
          };
        }
        return option;
      });
    });
  };

  const dispatchTitleUpdate = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ): void => {
    const newValue = e.target.value;
    dispatch({ type: ActionType.UPDATE_TITLE, payload: newValue });
  };

  const dispatchAssigneeUpdate = (assignee: Filter): void => {
    dispatch({ type: ActionType.UPDATE_ASSIGNEE, payload: assignee });
  };

  const dispatchPriorityUpdate = (priority: Filter): void => {
    dispatch({ type: ActionType.UPDATE_PRIORITY, payload: priority });
  };

  const dispatchSubjectTypeUpdate = (subjectType: Filter): void => {
    dispatch({ type: ActionType.UPDATE_SUBJECT_TYPE, payload: subjectType });
  };

  const dispatchSubjectsUpdate = (subjects: Filter[]): void => {
    dispatch({ type: ActionType.UPDATE_SUBJECTS, payload: subjects });
  };

  const dispatchDueDateUpdate = (dueDate: Filter): void => {
    dispatch({ type: ActionType.UPDATE_DUE_DATE, payload: dueDate });
  };

  const dispatchResetParams = (): void => {
    dispatch({ type: ActionType.RESET_PARAMS });
  };

  return {
    createTaskConfig: state,
    taskConfigOverrides: overrides,
    taskStatus: status,
    isTaskLoading,
    dispatchTitleUpdate,
    dispatchAssigneeUpdate,
    dispatchPriorityUpdate,
    dispatchSubjectTypeUpdate,
    dispatchSubjectsUpdate,
    dispatchDueDateUpdate,
    dispatchResetParams,
    handleStatusChange,
    userOptions: buildUserOptions(),
    subjectsOptions,
    dateOptions,
    isUsersLoading,
    isSubjectTypeLoading,
    shouldDisplaySubjectOptions,
    selectedSubjectTypeDisplayValue,
  };
};

export type TaskModalProps = {
  createTaskConfig: CreateTaskConfig;
  taskStatus: TaskStatus | null;
  taskConfigOverrides: Record<string, Filter>;
  isTaskLoading: boolean;
  dispatchTitleUpdate: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    source: "title" | "content",
  ) => void;
  dispatchAssigneeUpdate: (assignee: Filter) => void;
  dispatchPriorityUpdate: (priority: Filter) => void;
  dispatchSubjectTypeUpdate: (subjectType: Filter) => void;
  dispatchSubjectsUpdate: (subjects: Filter[]) => void;
  dispatchDueDateUpdate: (dueDate: Filter) => void;
  dispatchResetParams: () => void;
  handleStatusChange: (option: Filter) => void;
  userOptions: Filter[];
  dateOptions: Filter[];
  subjectsOptions: Filter[];
  isUsersLoading: boolean;
  isSubjectTypeLoading: boolean;
  shouldDisplaySubjectOptions: boolean;
  selectedSubjectTypeDisplayValue: string;
};
